import DOMPurify, { type Config, type UponSanitizeAttributeHookEvent } from 'dompurify';

const CONFIG: Config = {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'style'],
  ALLOW_DATA_ATTR: false,
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
};

const SAFE_STYLE_URL = /^(?:https?:|data:image\/)/i;
const CSS_URL = /url\s*\(\s*(['"]?)([^)'"]+)\1\s*\)/gi;

/**
 * Removes ASCII control characters so obfuscations like `java\tscript:` collapse.
 *
 * @param value - Raw or CSS-decoded text.
 */
function stripControlChars(value: string): string {
  let result = '';
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code > 0x1f && code !== 0x7f) {
      result += value[i];
    }
  }
  return result;
}

/**
 * Decodes CSS escapes (`\6A` → `j`, `\n` → `n`) so obfuscated `javascript:` is detectable.
 *
 * @param value - Raw CSS from a `style` attribute.
 * @returns The value with CSS escapes resolved to characters.
 */
function decodeCssEscapes(value: string): string {
  return value
    .replace(/\\([0-9a-fA-F]{1,6})[ \t\n\r\f]?/g, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/\\(.)/g, '$1');
}

/**
 * Returns true when a single CSS declaration is unsafe for email HTML previews.
 *
 * @param declaration - One `property: value` segment from a `style` attribute.
 */
function isDangerousStyleDeclaration(declaration: string): boolean {
  const decoded = decodeCssEscapes(declaration);
  const withoutControls = stripControlChars(decoded);
  const compact = withoutControls.replace(/\s+/g, '').toLowerCase();

  if (
    compact.includes('javascript:') ||
    compact.includes('vbscript:') ||
    compact.includes('expression(') ||
    compact.includes('behavior:') ||
    compact.includes('-moz-binding')
  ) {
    return true;
  }

  // Only allow http(s) and image data URLs inside url(...).
  CSS_URL.lastIndex = 0;
  let urlMatch: RegExpExecArray | null = CSS_URL.exec(withoutControls);
  while (urlMatch !== null) {
    const rawUrl = stripControlChars(decodeCssEscapes(urlMatch[2]!)).replace(/\s+/g, '').toLowerCase();
    if (!SAFE_STYLE_URL.test(rawUrl)) {
      return true;
    }
    urlMatch = CSS_URL.exec(withoutControls);
  }

  return false;
}

/**
 * Keeps only safe CSS declarations; returns null when nothing safe remains (drop the attr).
 *
 * @param styleValue - Full `style` attribute value.
 */
function scrubStyleAttribute(styleValue: string): string | null {
  const kept = styleValue
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((declaration) => !isDangerousStyleDeclaration(declaration));

  if (kept.length === 0) {
    return null;
  }
  return kept.join('; ');
}

/**
 * DOMPurify hook: scrub or drop `style` attributes that embed executable CSS.
 */
function scrubDangerousStyleAttribute(_node: Element, data: UponSanitizeAttributeHookEvent): void {
  if (data.attrName !== 'style') {
    return;
  }
  const scrubbed = scrubStyleAttribute(data.attrValue);
  if (scrubbed == null) {
    data.keepAttr = false;
    return;
  }
  data.attrValue = scrubbed;
}

/**
 * Sanitizes HTML used in email previews.
 * Strips dangerous tags/attrs and scrubs inline CSS that can carry `javascript:` / `expression()` payloads
 * (including CSS-escape and control-character obfuscation).
 *
 * @param html - Untrusted HTML from an email body or message payload.
 * @returns Safe HTML suitable for `dangerouslySetInnerHTML` / HTML parsers.
 *
 * @example
 * ```ts
 * sanitizeEmailHtml('<p style="background:url(javascript:alert(1));padding:1em">hi</p>');
 * // => '<p style="padding:1em">hi</p>'
 * ```
 */
export function sanitizeEmailHtml(html: unknown): string {
  if (typeof html !== 'string' || html.length === 0) return '';
  const blanked = html.replace(/src=["']cid:[^"']+["']/g, 'src=""');

  DOMPurify.addHook('uponSanitizeAttribute', scrubDangerousStyleAttribute);
  try {
    return DOMPurify.sanitize(blanked, CONFIG);
  } finally {
    DOMPurify.removeHook('uponSanitizeAttribute');
  }
}
